import { Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie-service";

@Injectable({
    providedIn: 'root'
  })
  export class ConfigStoreService {
    constructor(private cookieService: CookieService) {}
  
    // ===== COOKIES (for session/auth data) =====
    
    /**
     * Store data in cookie. Defaults to path '/', so every route reads the same cookie.
     */
    public setCookie(key: string, value: any, expires?: number, path?: string, domain?: string, secure?: boolean): void {
      this.removeRouteScopedCookie(key, path, domain);
      this.cookieService.set(key, JSON.stringify(value), expires, path ?? '/', domain, secure);
    }
  
    /**
     * Retrieve data from cookie
     */
    public getCookie<T>(key: string): T | null {
      const value = this.cookieService.get(key);
      return value ? JSON.parse(value) : null;
    }
  
    /**
     * Remove data from cookie
     */
    public removeCookie(key: string, path?: string, domain?: string): void {
      this.removeRouteScopedCookie(key, path, domain);
      this.cookieService.delete(key, path ?? '/', domain);
    }

    /**
     * Cookies used to be written without a path, so the browser scoped them to the current route's folder
     * (e.g. '/template'). Such a cookie shadows the '/' cookie on that route, so remove it when writing.
     */
    private removeRouteScopedCookie(key: string, path?: string, domain?: string): void {
      const routeFolder = window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'));
      if (path === undefined && routeFolder.length > 0) {
        this.cookieService.delete(key, routeFolder, domain);
      }
    }

    // ===== LOCAL STORAGE (for UI preferences) =====
    
    /**
     * Store data in localStorage
     */
    public setLocal<T>(key: string, value: T): void {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.error('Failed to save to localStorage:', error);
      }
    }

    /**
     * Retrieve data from localStorage with optional default value
     */
    public getLocal<T>(key: string, defaultValue?: T): T | null {
      try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : (defaultValue ?? null);
      } catch (error) {
        console.error('Failed to read from localStorage:', error);
        return defaultValue ?? null;
      }
    }

    /**
     * Remove data from localStorage
     */
    public removeLocal(key: string): void {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.error('Failed to remove from localStorage:', error);
      }
    }

    // ===== SESSION STORAGE (per-tab UI state, e.g. remembered list searches) =====

    /**
     * Store data in sessionStorage
     */
    public setSession<T>(key: string, value: T): void {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.error('Failed to save to sessionStorage:', error);
      }
    }

    /**
     * Retrieve data from sessionStorage with optional default value
     */
    public getSession<T>(key: string, defaultValue?: T): T | null {
      try {
        const value = sessionStorage.getItem(key);
        return value ? JSON.parse(value) : (defaultValue ?? null);
      } catch (error) {
        console.error('Failed to read from sessionStorage:', error);
        return defaultValue ?? null;
      }
    }

    /**
     * Remove data from sessionStorage
     */
    public removeSession(key: string): void {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        console.error('Failed to remove from sessionStorage:', error);
      }
    }

    /**
     * Clear all localStorage data
     */
    public clearLocal(): void {
      try {
        localStorage.clear();
      } catch (error) {
        console.error('Failed to clear localStorage:', error);
      }
    }
  }
  